import { AxiosError, AxiosHeaders, AxiosInstance, InternalAxiosRequestConfig } from 'axios';

/**
 * Cloudflare Turnstile tokens for the anonymous backend calls that cost money or create records.
 *
 * On 23-25/10/2026 a script replayed the onboarding OTP call ~10,700 times (SMS pumping, ~2,300 EUR).
 * The backend (`RestWorld.BotChallenge` in core-services) now asks those calls for a token that only a
 * browser running the Turnstile widget on one of our pages can mint. This module mints it and the Axios
 * interceptor in `axios.ts` attaches it, so no call site has to know about it.
 *
 * - The token travels in the `x-bot-challenge` header, minted for the action the backend expects on that
 *   endpoint (`BOT_CHALLENGE_ENDPOINTS`). A token is single-use: one per request.
 * - Without `NEXT_PUBLIC_TURNSTILE_SITE_KEY`, or on the server (SSR), nothing happens.
 * - The Cloudflare script is loaded on the first protected call, never on page load.
 * - The widgets are Managed: most visitors pass without seeing anything (`appearance: 'interaction-only'`),
 *   a visitor Cloudflare is unsure about gets a checkbox. When that happens the silent widget is dropped and the
 *   checkbox is shown in `BotChallengeHost` (ui-components), mounted once per app: this module only publishes
 *   the request (`subscribeBotChallengeInteraction`), so it stays free of React.
 * - **Measure only, never wait (29/10/2026).** A protected call leaves at once with `none:deferred`; the token
 *   is minted when the browser is next idle and only measured (`waited: false` on the outcome, up to `MEASURE_TIMEOUT_MS`, no
 *   checkbox: Cloudflare asking for one is recorded as `interaction_needed`). Waiting for the token before the
 *   call cost a median 2-4 s on every protected action and 6-7 s on Android, and the share of started CV
 *   uploads actually sent on Android fell from 74% to 66% (PostHog `bot_challenge_outcome`, 26-29/10/2026).
 *   The backend is in `log` on every action, so nothing it does depends on the token today.
 * - **The waiting path is kept for `enforce`.** A 403 with `code: "bot_challenge_failed"` is retried once, and
 *   the retry waits for a real token (`TOKEN_TIMEOUT_MS` while silent, `INTERACTIVE_TIMEOUT_MS` once a person has
 *   the checkbox from `BotChallengeHost`). Without a token the retry still goes, carrying `none:<reason>`
 *   (timeout, abandoned, error, unavailable) so the backend logs why. `getBotChallengeToken` waits too, for a
 *   call that does not go through the shared Axios instance.
 * - Every outcome is published (`subscribeBotChallengeOutcome`) for the host to send to analytics, with
 *   `waited` saying whether a request was held for it.
 */

export const BOT_CHALLENGE_HEADER = 'x-bot-challenge';
const FAILED_CODE = 'bot_challenge_failed';
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const TOKEN_TIMEOUT_MS = 8000;
const INTERACTIVE_TIMEOUT_MS = 90000;
// How long a measurement-only widget is followed: long enough to see the real duration on slow phones, which
// the 8 s deadline used to cut off.
const MEASURE_TIMEOUT_MS = 60000;
// The header of a call that left without waiting for its token.
const DEFERRED = 'none:deferred';
// A measurement starts when the browser is idle, never in the same moment as the person's action: drawing the
// widget costs main-thread time on slow phones. Without requestIdleCallback (older Safari) it starts after this.
const MEASURE_IDLE_TIMEOUT_MS = 3000;
const MEASURE_FALLBACK_DELAY_MS = 1000;

const whenIdle = (task: () => void) => {
  const idle = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number })
    .requestIdleCallback;
  if (idle) idle(task, { timeout: MEASURE_IDLE_TIMEOUT_MS });
  else setTimeout(task, MEASURE_FALLBACK_DELAY_MS);
};

/** Method + path of each protected endpoint, and the action name the backend checks the token against. */
export const BOT_CHALLENGE_ENDPOINTS: ReadonlyArray<{ method: string; path: string; action: string }> = [
  { method: 'get', path: '/accounts/verification_phone_otp', action: 'otp_request' },
  { method: 'post', path: '/accounts/verify_phone_otp', action: 'otp_verify' },
  { method: 'post', path: '/workers/upload_cv', action: 'cv_upload' },
  { method: 'post', path: '/workers/cv', action: 'cv_missing_data' },
  { method: 'post', path: '/workers/worker', action: 'worker_create' },
  { method: 'post', path: '/organizations', action: 'organization_create' },
  { method: 'post', path: '/organizations/two_step', action: 'organization_two_step' },
  { method: 'put', path: '/organizations/prospects', action: 'prospect_upsert' },
  { method: 'post', path: '/employers/job_positions/generate_ad_content', action: 'ad_generate' },
  { method: 'post', path: '/employers/job_positions/validate_ad', action: 'ad_validate' },
  { method: 'get', path: '/employers/restaurants/additional_information', action: 'venue_information' },
];

type TurnstileRenderOptions = {
  sitekey: string;
  action: string;
  appearance?: 'always' | 'execute' | 'interaction-only';
  language?: string;
  callback: (token: string) => void;
  'error-callback': () => boolean | void;
  'timeout-callback': () => void;
  'expired-callback'?: () => void;
  'before-interactive-callback'?: () => void;
  'refresh-expired': 'never';
  retry: 'never';
};

type Turnstile = {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string | undefined;
  remove: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: Turnstile;
  }
}

const siteKey = () => process.env['NEXT_PUBLIC_TURNSTILE_SITE_KEY'];

/** Whether this page can mint tokens at all. */
export const isBotChallengeEnabled = () => typeof window !== 'undefined' && !!siteKey();

const pathOf = (url: string | undefined) => {
  if (!url) return '';
  try {
    return new URL(url, 'http://relative.invalid').pathname.replace(/\/+$/, '');
  } catch {
    return url.split('?')[0].replace(/\/+$/, '');
  }
};

/** The action a request needs a token for, or undefined when the endpoint is not protected. */
export const botChallengeActionFor = (method: string | undefined, url: string | undefined) => {
  const path = pathOf(url);
  return BOT_CHALLENGE_ENDPOINTS.find(
    (endpoint) => endpoint.method === (method || 'get').toLowerCase() && endpoint.path === path
  )?.action;
};

let scriptPromise: Promise<Turnstile | undefined> | undefined;

/** Loads the Cloudflare script once. Also used by `useBotChallenge` to warm it up on the funnels. */
export const loadTurnstile = (): Promise<Turnstile | undefined> => {
  if (!isBotChallengeEnabled()) return Promise.resolve(undefined);
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.defer = true;
    // Iubenda autoblocking: purpose 1 = strictly necessary, so the consent banner never holds it back.
    script.setAttribute('data-iub-purposes', '1');
    script.onload = () => resolve(window.turnstile);
    script.onerror = () => {
      scriptPromise = undefined;
      resolve(undefined);
    };
    document.head.appendChild(script);
  });
  return scriptPromise;
};

/** Why a request went without a token, as the backend logs it. */
export type BotChallengeFailure = 'timeout' | 'abandoned' | 'error' | 'unavailable' | 'interaction_needed';

export type BotChallengeOutcome = {
  action: string;
  outcome: 'passed' | 'passed_interactive' | BotChallengeFailure;
  elapsedMs: number;
  /** Whether a request was held until this outcome (false: the token was only measured). */
  waited: boolean;
};

/** A checkbox waiting to be shown: the host calls `mount` with a visible element, `cancel` when it is closed. */
export type BotChallengeInteraction = {
  action: string;
  mount: (container: HTMLElement) => void;
  cancel: () => void;
};

type MintResult = { token: string; interactive: boolean } | { failure: BotChallengeFailure };

const interactionListeners = new Set<(interaction: BotChallengeInteraction | null) => void>();
const outcomeListeners = new Set<(outcome: BotChallengeOutcome) => void>();
// One checkbox at a time: two protected calls can start together (prospects and venue information do).
const interactionQueue: BotChallengeInteraction[] = [];

const publishInteraction = () => interactionListeners.forEach((listener) => listener(interactionQueue[0] ?? null));

/** The host listens here; it receives the checkbox to show, or null when there is none. */
export const subscribeBotChallengeInteraction = (listener: (interaction: BotChallengeInteraction | null) => void) => {
  interactionListeners.add(listener);
  listener(interactionQueue[0] ?? null);
  return () => {
    interactionListeners.delete(listener);
  };
};

export const subscribeBotChallengeOutcome = (listener: (outcome: BotChallengeOutcome) => void) => {
  outcomeListeners.add(listener);
  return () => {
    outcomeListeners.delete(listener);
  };
};

type MintOptions = {
  /** A request is held until the outcome. False: the token is only measured, nobody waits for it. */
  waited: boolean;
};

const mintToken = (action: string, { waited }: MintOptions): Promise<MintResult> =>
  new Promise<MintResult>((resolve) => {
    const startedAt = Date.now();
    let settled = false;
    let widgetId: string | undefined;
    let container: HTMLElement | undefined;
    let interaction: BotChallengeInteraction | undefined;
    let timer = setTimeout(() => finish({ failure: 'timeout' }), waited ? TOKEN_TIMEOUT_MS : MEASURE_TIMEOUT_MS);

    const finish = (result: MintResult) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (widgetId) window.turnstile?.remove(widgetId);
      container?.remove();
      if (interaction) {
        const index = interactionQueue.indexOf(interaction);
        if (index >= 0) interactionQueue.splice(index, 1);
        publishInteraction();
      }
      const outcome = 'token' in result ? (result.interactive ? 'passed_interactive' : 'passed') : result.failure;
      outcomeListeners.forEach((listener) => listener({ action, outcome, elapsedMs: Date.now() - startedAt, waited }));
      resolve(result);
    };

    const handlers = (interactive: boolean) => ({
      callback: (token: string) => finish({ token, interactive }),
      'error-callback': () => {
        finish({ failure: 'error' });
        return true;
      },
      'timeout-callback': () => finish({ failure: 'timeout' }),
      'expired-callback': () => finish({ failure: 'timeout' }),
      'refresh-expired': 'never' as const,
      retry: 'never' as const,
    });

    // A widget can answer inside render(), before its id is known: remove it then, or remember it for finish().
    // The silent widget can also go interactive inside render(): then it is already replaced and goes too.
    const keep = (turnstile: Turnstile, id: string | undefined, silent: boolean) => {
      if (settled || (silent && interaction)) {
        if (id) turnstile.remove(id);
      } else widgetId = id;
    };

    // Cloudflare wants a click: drop the silent widget and hand a visible one to the host, if there is a host.
    // A measurement never interrupts the person with a checkbox: it records that one was asked for.
    const goInteractive = (turnstile: Turnstile, sitekey: string) => {
      if (!waited) return finish({ failure: 'interaction_needed' });
      if (settled || interaction || interactionListeners.size === 0) return;
      clearTimeout(timer);
      timer = setTimeout(() => finish({ failure: 'timeout' }), INTERACTIVE_TIMEOUT_MS);
      if (widgetId) turnstile.remove(widgetId);
      widgetId = undefined;
      container?.remove();
      container = undefined;
      interaction = {
        action,
        mount: (visible) => {
          if (settled || widgetId) return;
          keep(turnstile, turnstile.render(visible, { sitekey, action, ...handlers(true) }), false);
        },
        cancel: () => finish({ failure: 'abandoned' }),
      };
      interactionQueue.push(interaction);
      publishInteraction();
    };

    loadTurnstile().then((turnstile) => {
      const sitekey = siteKey();
      if (settled) return;
      if (!turnstile || !sitekey) return finish({ failure: 'unavailable' });

      // Off screen, not display:none: an interaction-only widget stays invisible until it needs a person.
      container = document.createElement('div');
      container.setAttribute('aria-hidden', 'true');
      container.style.cssText = 'position:fixed;left:-10000px;top:0;width:300px;height:65px;';
      document.body.appendChild(container);
      keep(
        turnstile,
        turnstile.render(container, {
          sitekey,
          action,
          appearance: 'interaction-only',
          'before-interactive-callback': () => goInteractive(turnstile, sitekey),
          ...handlers(false),
        }),
        true
      );
    });
  });

/**
 * Mints one token for `action`, or resolves undefined when it cannot (no key, SSR, widget failure, timeout, the
 * person closed the checkbox).
 */
export const getBotChallengeToken = async (action: string): Promise<string | undefined> => {
  if (!isBotChallengeEnabled()) return undefined;
  const result = await mintToken(action, { waited: true });
  return 'token' in result ? result.token : undefined;
};

type RetriableConfig = InternalAxiosRequestConfig & { botChallengeRetried?: boolean };

const setHeader = (config: InternalAxiosRequestConfig, value: string) => {
  if (config.headers) (config.headers as AxiosHeaders).set(BOT_CHALLENGE_HEADER, value);
  return config;
};

// The retry of a call the backend refused: this one waits for a real token.
const attachToken = async (config: InternalAxiosRequestConfig, action: string) => {
  const result = await mintToken(action, { waited: true });
  // No token: say why, so the backend can tell a person whose widget failed from a script that never ran one.
  return setHeader(config, 'token' in result ? result.token : `none:${result.failure}`);
};

/**
 * Request interceptor. A protected call leaves at once with `none:deferred` while its token is minted in
 * parallel and measured; only the retry of a refused call waits for one (see the module doc).
 */
export const botChallengeRequestInterceptor = async (config: InternalAxiosRequestConfig) => {
  if (!isBotChallengeEnabled()) return config;
  const action = botChallengeActionFor(config.method, config.url);
  if (!action) return config;
  if ((config as RetriableConfig).botChallengeRetried) return attachToken(config, action);

  whenIdle(() => void mintToken(action, { waited: false }));
  return setHeader(config, DEFERRED);
};

/**
 * Response error handler: when the backend refused the token (expired, already spent, widget glitch), replay
 * the request once with a new one. Returns undefined when the error is not ours to handle.
 */
export const retryOnBotChallengeFailure = async (instance: AxiosInstance, error: AxiosError) => {
  const config = error.config as RetriableConfig | undefined;
  const data = error.response?.data as { code?: string } | undefined;
  if (!config || error.response?.status !== 403 || data?.code !== FAILED_CODE || config.botChallengeRetried)
    return undefined;

  const action = botChallengeActionFor(config.method, config.url);
  if (!action || !isBotChallengeEnabled()) return undefined;

  // The request interceptor mints the fresh token, and waits for it, when the request goes through again.
  config.botChallengeRetried = true;
  (config.headers as AxiosHeaders | undefined)?.delete?.(BOT_CHALLENGE_HEADER);
  return instance.request(config);
};
